import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components/macro";

import { atomicConfig } from "../../../config";
import { trackEvent } from "../../../utils/analytics";
import { call_api_auth } from "../../../utils/services";
import { ReactComponent as Logo } from "../../../vendor/logo-dark.svg";

import { mapAdsErrorToMessage } from "./AmazonAdsCallback";

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const Brand = styled(Logo)`
  width: 150px;
  height: 50px;
  margin-bottom: 20px;
`;

const LogoCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function extractErrorCode(err) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    return data.error || data.message || null;
  }
  return null;
}

function AmazonAdsConnect() {
  const [connecting, setConnecting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const url = `${atomicConfig.userServiceUrl}/amazon/ads/status`;
      const res = await call_api_auth(url, "GET");
      const body = res?.data || {};
      setConnected(Boolean(body.connected));
      setAccounts(Array.isArray(body.accounts) ? body.accounts : []);
    } catch (err) {
      // Status failure shouldn't block the connect flow.
      setConnected(false);
      setAccounts([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleConnect = async () => {
    trackEvent("amazon_ads_connect_initiated");
    setErrorMessage(null);
    setConnecting(true);
    try {
      const url = `${atomicConfig.userServiceUrl}/amazon/ads/connect`;
      const res = await call_api_auth(url, "GET");
      const redirectUrl = res?.data?.redirect_url;
      if (!redirectUrl) {
        throw new Error("Missing redirect URL from server.");
      }
      trackEvent("amazon_ads_connect_redirecting");
      window.location.href = redirectUrl;
    } catch (err) {
      const code = extractErrorCode(err);
      const msg = mapAdsErrorToMessage(
        code,
        err?.message || "Failed to start the Amazon Ads connection."
      );
      trackEvent("amazon_ads_connect_error", { error: code || err?.message });
      setErrorMessage(msg);
      setConnecting(false);
    }
  };

  const handleDisconnect = async (sellingPartnerId) => {
    const trackingId = sellingPartnerId || "__all__";
    setDisconnectingId(trackingId);
    setErrorMessage(null);
    try {
      const url = `${atomicConfig.userServiceUrl}/amazon/ads/disconnect`;
      const payload = sellingPartnerId
        ? { selling_partner_id: sellingPartnerId }
        : {};
      await call_api_auth(url, "POST", payload);
      trackEvent("amazon_ads_disconnect_success", {
        scope: sellingPartnerId ? "single" : "all",
      });
      await refreshStatus();
    } catch (err) {
      const code = extractErrorCode(err);
      trackEvent("amazon_ads_disconnect_error", {
        error: code || err?.message,
      });
      setErrorMessage(
        mapAdsErrorToMessage(
          code,
          err?.message || "Failed to disconnect the Amazon Ads account."
        )
      );
    } finally {
      setDisconnectingId(null);
    }
  };

  const renderConnectButton = () => (
    <Button
      variant="contained"
      size="large"
      onClick={handleConnect}
      disabled={connecting}
      sx={{
        backgroundColor: "#FF9900",
        color: "#111",
        fontWeight: 600,
        px: 5,
        py: 1.5,
        "&:hover": { backgroundColor: "#e88b00" },
      }}
    >
      {connecting ? (
        <CircularProgress size={24} sx={{ color: "#111" }} />
      ) : connected ? (
        "Connect another Ads account"
      ) : (
        "Connect with Amazon Ads"
      )}
    </Button>
  );

  return (
    <React.Fragment>
      <Wrapper>
        <LogoCenter>
          <Brand />
        </LogoCenter>
        <Helmet title="Connect Amazon Ads" />

        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Connect Amazon Ads
        </Typography>
        <Typography
          component="h2"
          variant="body1"
          align="center"
          sx={{ mb: 4, color: "text.secondary" }}
        >
          Link your Amazon Ads (Sponsored Ads / DSP) account so we can pull your
          campaign performance alongside your seller data.
        </Typography>

        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
            alt="Amazon"
            sx={{ width: 120, mb: 2 }}
          />

          {statusLoading ? (
            <CircularProgress size={32} />
          ) : connected ? (
            <Box sx={{ width: "100%", maxWidth: 520 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Chip
                  label="Connected"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {accounts.length} account{accounts.length === 1 ? "" : "s"}{" "}
                  linked
                </Typography>
              </Stack>

              <List dense disablePadding>
                {accounts.map((acc) => {
                  const id = acc.selling_partner_id;
                  return (
                    <ListItem
                      key={`${id}-${acc.profile_id}`}
                      divider
                      secondaryAction={
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={disconnectingId === id}
                          onClick={() => handleDisconnect(id)}
                        >
                          {disconnectingId === id ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Disconnect"
                          )}
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={`Seller ${id}`}
                        secondary={[
                          `Profile ${acc.profile_id}`,
                          acc.country_code,
                          acc.connected_at
                            ? `Linked ${new Date(
                                acc.connected_at
                              ).toLocaleDateString()}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" \u00B7 ")}
                      />
                    </ListItem>
                  );
                })}
              </List>

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={2} justifyContent="center">
                {renderConnectButton()}
                <Button
                  variant="text"
                  color="error"
                  onClick={() => handleDisconnect(null)}
                  disabled={disconnectingId === "__all__"}
                >
                  {disconnectingId === "__all__" ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Disconnect all"
                  )}
                </Button>
              </Stack>
            </Box>
          ) : (
            <React.Fragment>
              <Typography
                variant="body2"
                align="center"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                By connecting your account, you authorize us to access your
                Amazon Ads campaign data for reporting and analytics.
              </Typography>
              {renderConnectButton()}
            </React.Fragment>
          )}
        </Box>
      </Wrapper>
    </React.Fragment>
  );
}

export default AmazonAdsConnect;
